import React from 'react';
import PropTypes from 'prop-types';
import {Save, Download, UploadCloud} from 'lucide-react';
import Box from '../box/box.jsx';
import Modal from '../../containers/modal.jsx';
import styles from './magblocks-modal.css';

/**
 * view='options'  — first screen: Save to My Projects / Save as File
 * view='name'     — name input screen (new project or save-as-new)
 * view='update'   — existing project: Update / Save as New / Cancel
 */
const MagblocksSaveModal = ({
    view,
    projectName,
    nameValue,
    nameError,
    isSaving,
    onNameChange,
    onCloudSave,
    onExport,
    onUpdate,
    onSaveAsNew,
    onNameSubmit,
    onBack,
    onCancel
}) => (
    <Modal
        className={styles.modalContent}
        contentLabel="Save Project"
        id="magblocksSave"
        onRequestClose={onCancel}
    >
        <Box className={styles.modalBody}>
            {view === 'options' && (
                <div>
                    <div className={styles.stackActions}>
                        <button
                            className={styles.btnPrimary}
                            onClick={onCloudSave}
                        >
                            <UploadCloud
                                size={16}
                                strokeWidth={2.5}
                            />
                            {'Save to My Projects'}
                        </button>
                        <button
                            className={styles.btn}
                            onClick={onExport}
                        >
                            <Download
                                size={16}
                                strokeWidth={2.5}
                            />
                            {'Save as File (.mgnm)'}
                        </button>
                    </div>
                </div>
            )}

            {view === 'name' && (
                <div>
                    <p className={styles.viewLabel}>{'Name your project'}</p>
                    <input
                        autoFocus
                        className={styles.nameInput}
                        disabled={isSaving}
                        placeholder={'My Project'}
                        type="text"
                        value={nameValue}
                        onChange={onNameChange}
                        onKeyDown={e => { if (e.key === 'Enter' && !isSaving) onNameSubmit(); }}
                    />
                    {nameError && (
                        <p className={styles.errorMsg}>{nameError}</p>
                    )}
                    <div className={styles.stackActions}>
                        <button
                            className={styles.btnPrimary}
                            disabled={isSaving || !nameValue.trim()}
                            onClick={onNameSubmit}
                        >
                            <Save
                                size={16}
                                strokeWidth={2.5}
                            />
                            {isSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                            className={styles.btn}
                            disabled={isSaving}
                            onClick={onBack}
                        >
                            {'Back'}
                        </button>
                    </div>
                </div>
            )}

            {view === 'update' && (
                <div>
                    <div className={styles.stackActions}>
                        <button
                            className={styles.btnPrimary}
                            disabled={isSaving}
                            onClick={onUpdate}
                        >
                            <Save
                                size={16}
                                strokeWidth={2.5}
                            />
                            {isSaving ? 'Saving…' : `Update "${projectName}"`}
                        </button>
                        <button
                            className={styles.btn}
                            disabled={isSaving}
                            onClick={onSaveAsNew}
                        >
                            {'Save as New Project'}
                        </button>
                        <button
                            className={styles.btn}
                            disabled={isSaving}
                            onClick={onBack}
                        >
                            {'Back'}
                        </button>
                    </div>
                </div>
            )}
        </Box>
    </Modal>
);

MagblocksSaveModal.propTypes = {
    view: PropTypes.oneOf(['options', 'name', 'update']).isRequired,
    projectName: PropTypes.string,
    nameValue: PropTypes.string,
    nameError: PropTypes.string,
    isSaving: PropTypes.bool,
    onNameChange: PropTypes.func,
    onCloudSave: PropTypes.func,
    onExport: PropTypes.func,
    onUpdate: PropTypes.func,
    onSaveAsNew: PropTypes.func,
    onNameSubmit: PropTypes.func,
    onBack: PropTypes.func,
    onCancel: PropTypes.func.isRequired
};

MagblocksSaveModal.defaultProps = {
    nameValue: '',
    nameError: null,
    isSaving: false
};

export default MagblocksSaveModal;
